/**
 * Serviço REST do módulo de agendamentos.
 *
 * Endpoints backend em apps.scheduler (/api/scheduler/*).
 * Todas as chamadas são protegidas por FEATURE_SCHEDULED_TASKS e
 * can_manage_schedules — 404 quando flag off, 403 sem permissão.
 */

import api, { apiEndpoints } from '../lib/api'
import type {
  ScheduledTask,
  ScheduledTaskCreatePayload,
  ScheduledTaskExecution,
  ScheduledTaskUpdatePayload,
  SchedulableTool,
} from '../types/scheduler'

export const schedulerService = {
  async listTools(): Promise<SchedulableTool[]> {
    const { data } = await api.get<SchedulableTool[]>(
      apiEndpoints.scheduler.tools
    )
    return data
  },

  async listTasks(): Promise<ScheduledTask[]> {
    const { data } = await api.get<
      ScheduledTask[] | { results: ScheduledTask[] }
    >(apiEndpoints.scheduler.tasks)
    return Array.isArray(data) ? data : data.results
  },

  async getTask(id: string): Promise<ScheduledTask> {
    const { data } = await api.get<ScheduledTask>(
      apiEndpoints.scheduler.taskDetail(id)
    )
    return data
  },

  async createTask(
    payload: ScheduledTaskCreatePayload
  ): Promise<ScheduledTask> {
    const { data } = await api.post<ScheduledTask>(
      apiEndpoints.scheduler.tasks,
      payload
    )
    return data
  },

  async updateTask(
    id: string,
    payload: ScheduledTaskUpdatePayload
  ): Promise<ScheduledTask> {
    const { data } = await api.patch<ScheduledTask>(
      apiEndpoints.scheduler.taskDetail(id),
      payload
    )
    return data
  },

  async deleteTask(id: string): Promise<void> {
    await api.delete(apiEndpoints.scheduler.taskDetail(id))
  },

  async runNow(id: string): Promise<{ status: string; task_id: string }> {
    const { data } = await api.post<{ status: string; task_id: string }>(
      apiEndpoints.scheduler.runNow(id)
    )
    return data
  },

  async listExecutions(id: string): Promise<ScheduledTaskExecution[]> {
    const { data } = await api.get<
      ScheduledTaskExecution[] | { results: ScheduledTaskExecution[] }
    >(apiEndpoints.scheduler.executions(id))
    return Array.isArray(data) ? data : data.results
  },
}

export default schedulerService
